import { GUN_SKIN_KEYS, GUN_SKINS, type GunSkin, isPreviewSkin, POLISHED_SKINS } from './gun-skins';
import { HEAD_KEYS, HEADS, type HeadKey, isHeadKey } from './heads';
import { LOOKS, type Look, lookOptions } from './looks';
import { seasonItem } from './season';

/**
 * Seeds (ADR-0093): the account's currency. Paid once for each achievement (`ACHIEVEMENTS`, ADR-0172), for every battle
 * royale won (`win`; nothing in a custom room, which anyone can fill with bots) and once a day for a Zombie Nights run on
 * today's map (`todaysMap`, content `todaysMapPays`), and spent in the Store on things you then wear. Only the server
 * moves them.
 */
export const SEEDS = {
  win: { royale: 20 },
  todaysMap: 50,
} as const;

/** Seeds a round won in a room of `kind` pays (a server `RoomKind`; the dev lobby, a custom room or none: nothing). */
export const winSeeds = (kind: string | null | undefined): number => (kind === 'royale' ? SEEDS.win.royale : 0);

/**
 * Sets (ADR-0112): a costume in three pieces, a head (`HEADS`), an outfit (`LOOKS.outfit`) and a gun skin
 * (`GUN_SKINS`), each keyed like its set; sold one by one or together for less (`setPrice`). Wearing all three whole
 * gives the set's knock-out flourish (`wornSet`). A `preview` set (ADR-0160: the mythic sets, whose pieces burn, sway
 * and crackle: their art's `flair`) is in the build but nobody's: each of its pieces is a thing in preview (`isPreviewItem`)
 * an admin releases at a price, and the set is sold whole once all three are (`setSold`).
 */
export const SETS = {
  gnome: { name: 'Gnome' },
  beekeeper: { name: 'Beekeeper' },
  astronaut: { name: 'Astronaut' },
  pumpkin: { name: 'Pumpkin' },
  inferno: { name: 'Inferno' },
  kitsune: { name: 'Kitsune' },
  voidwalker: { name: 'Voidwalker' },
  tempest: { name: 'Tempest' },
  glacier: { name: 'Glacier' },
  angler: { name: 'Abyssal Angler' },
  jester: { name: 'Jester' },
  plague: { name: 'Plague Doctor' },
  chronomancer: { name: 'Chronomancer' },
  glitch: { name: 'Glitch' },
  disco: { name: 'Disco' },
  origami: { name: 'Origami' },
  ooze: { name: 'Ooze' },
  barnstormer: { name: 'Barnstormer' },
  pirate: { name: 'Pirate', preview: true },
  magician: { name: 'Magician' },
  chef: { name: 'Chef', preview: true },
  knight: { name: 'Knight', preview: true },
  sheriff: { name: 'Sheriff', preview: true },
  luchador: { name: 'Luchador', preview: true },
} as const satisfies Record<string, { name: string; preview?: true }>;

export type SetKey = keyof typeof SETS;
/** Every set, in the Store's order. */
export const SET_KEYS = Object.keys(SETS) as SetKey[];
export const isSetKey = (key: string): key is SetKey => Object.hasOwn(SETS, key);
/** Is `key` a set in preview (ADR-0160): its pieces sold only once an admin releases them? */
export const isPreviewSet = (key: string): boolean => isSetKey(key) && 'preview' in SETS[key];
/** Every set in preview, in the Store's order. */
export const PREVIEW_SETS = SET_KEYS.filter(isPreviewSet);

/**
 * What the Store sells and the wardrobe wears (ADR-0112): a head (`HEADS`: a hat and a face together), an outfit (a
 * look's), or the skin on your guns (ADR-0095).
 */
export type ItemKind = 'head' | 'outfit' | 'gun';
/** A thing by its kind and key (`head:punk`, `outfit:hoodie`): what an account's `owned` lists. */
export type ItemId = `${ItemKind}:${string}`;
export interface StoreItem {
  id: ItemId;
  kind: ItemKind;
  key: string;
  name: string;
  price: number;
}

/**
 * The Store's prices in seeds (ADR-0093, ADR-0112): whole pieces, never a strand of hair. Free (a key missing here is
 * everyone's): the everyday heads and the headwear of its own (ADR-0089), the overalls and the `stock` guns. Sold: the
 * characters' heads, the hat recolours' heads (also won by playing, `HEADS.unlock`), the hoodie and the shirt, the
 * sets' heads and suits, and every other gun skin (ADR-0095), the polished ones (ADR-0148) the dearest. An outfit's first
 * option stays free (a look falls back to it). A partner has all of it (`ownedBy`, ADR-0149).
 */
const PRICES: {
  head: Partial<Record<HeadKey, number>>;
  outfit: Partial<Record<Look['outfit'], number>>;
  gun: Partial<Record<GunSkin, number>>;
} = {
  head: {
    lavender: 150,
    nightshade: 300,
    marigold: 1000,
    punk: 500,
    biker: 500,
    diva: 500,
    rockstar: 600,
    gnome: 800,
    beekeeper: 800,
    astronaut: 800,
    pumpkin: 800,
    // The mythic sets an admin released (ADR-0160, ADR-0166), at the prices they were released at.
    inferno: 100_000,
    kitsune: 100_000,
    voidwalker: 100_000,
    tempest: 100_000,
    glacier: 100_000,
    angler: 100_000,
    jester: 100_000,
    chronomancer: 100_000,
    glitch: 100_000,
    plague: 100_000,
    disco: 100_000,
    origami: 100_000,
    ooze: 100_000,
    barnstormer: 100_000,
    magician: 100_000,
  },
  outfit: {
    shirt: 200,
    hoodie: 250,
    gnome: 1000,
    beekeeper: 1000,
    astronaut: 1000,
    pumpkin: 1000,
    inferno: 100_000,
    kitsune: 100_000,
    voidwalker: 100_000,
    tempest: 100_000,
    glacier: 100_000,
    angler: 100_000,
    jester: 100_000,
    chronomancer: 100_000,
    glitch: 100_000,
    plague: 100_000,
    disco: 100_000,
    origami: 100_000,
    ooze: 100_000,
    barnstormer: 100_000,
    magician: 100_000,
  },
  // A luxury (ADR-0095): seen all game by everyone, priced over everything you wear.
  gun: {
    ivory: 1000,
    ember: 1200,
    frost: 1500,
    bubblegum: 1500,
    royal: 2000,
    midnight: 2000,
    gold: 3000,
    gnome: 1500,
    beekeeper: 1500,
    astronaut: 1500,
    pumpkin: 1500,
    // Effect skins (ADR-0113): the default gun redrawn, its bullets and sounds; a premium tier, the angel the flashiest.
    bubble: 10000,
    angel: 15000,
    plasma: 10000,
    hose: 10000,
    // Polished skins (ADR-0148 amended): over every other skin, the legends (Dragon Bloom on) over the rest of them. Those
    // in preview are priced by the admin who releases them (ADR-0154).
    ...Object.fromEntries(
      POLISHED_SKINS.filter((k) => !isPreviewSkin(k)).map((k, i) => [
        k,
        i < POLISHED_SKINS.indexOf('polished_dragonlore') ? 25_000 : 65_000,
      ]),
    ),
    // The mythic sets' guns an admin released (ADR-0160, ADR-0166).
    inferno: 100_000,
    kitsune: 100_000,
    voidwalker: 100_000,
    tempest: 100_000,
    glacier: 100_000,
    angler: 100_000,
    jester: 100_000,
    chronomancer: 100_000,
    glitch: 100_000,
    plague: 100_000,
    disco: 100_000,
    origami: 100_000,
    ooze: 100_000,
    barnstormer: 100_000,
    magician: 100_000,
  },
};

/** Every kind, in the wardrobe's order. */
export const ITEM_KINDS: readonly ItemKind[] = ['head', 'outfit', 'gun'];

/** `kind`'s keys, in the wardrobe's order: every head, every outfit or every gun skin. */
export const itemsOf = (kind: ItemKind): readonly string[] =>
  kind === 'head' ? HEAD_KEYS : kind === 'gun' ? GUN_SKIN_KEYS : lookOptions('outfit');

/** What `key` of `kind` is called (`Punk`, `Hoodie`, `Frost`). */
export function itemName(kind: ItemKind, key: string): string {
  const names: Record<string, { name: string } | string> =
    kind === 'head' ? HEADS : kind === 'gun' ? GUN_SKINS : LOOKS.outfit.options;
  const n = Object.hasOwn(names, key) ? names[key] : undefined;
  return (typeof n === 'string' ? n : n?.name) ?? key;
}

/**
 * The things in preview an admin released (ADR-0154) and their prices, by `ItemId` (`gun:inferno` → 90000): what the
 * Store sells besides `PRICES`, read from the server (the account's `released`).
 */
export type Released = Readonly<Record<string, number>>;
const NONE: Released = {};

/**
 * Is `id` a thing in preview (ADR-0154): nobody's, sold only while an admin has it released? A gun skin marked so
 * (`GUN_SKINS`), or the head or the outfit of a set in preview (ADR-0160, keyed like it).
 */
export function isPreviewItem(id: string): boolean {
  const [kind, key = ''] = id.split(':');
  return kind === 'gun' ? isPreviewSkin(key) : (kind === 'head' || kind === 'outfit') && isPreviewSet(key);
}

/** Every thing in preview, in the wardrobe's order. */
export const previewItems = (): ItemId[] =>
  ITEM_KINDS.flatMap((kind) =>
    itemsOf(kind).flatMap((key) => (isPreviewItem(`${kind}:${key}`) ? [`${kind}:${key}` as const] : [])),
  );

/** What `key` of `kind` costs in the Store, or null when it is not sold (a thing in preview: until it is `released`). */
export function priceOf(kind: ItemKind, key: string, released: Released = NONE): number | null {
  const table: Record<string, number> = PRICES[kind];
  if (Object.hasOwn(table, key)) return table[key] as number;
  const id = `${kind}:${key}`;
  return isPreviewItem(id) && Object.hasOwn(released, id) ? (released[id] as number) : null;
}

/** What playing unlocks head `key` at (its rounds or wins), if anything. */
const unlockOf = (key: HeadKey): { rounds?: number; wins?: number } | undefined => {
  const head = HEADS[key];
  return 'unlock' in head ? head.unlock : undefined;
};

/** What the Store sells, in the wardrobe's order. */
export const STORE_ITEMS: readonly StoreItem[] = ITEM_KINDS.flatMap((kind) =>
  itemsOf(kind).flatMap((key) => {
    const price = priceOf(kind, key);
    return price === null ? [] : [{ id: `${kind}:${key}` as const, kind, key, name: itemName(kind, key), price }];
  }),
);

/** The things in preview `released` as the Store sells them, in the wardrobe's order. */
export const releasedItems = (released: Released): StoreItem[] =>
  previewItems().flatMap((id) => {
    const [kind, key] = id.split(':') as [ItemKind, string];
    const price = priceOf(kind, key, released);
    return price === null ? [] : [{ id, kind, key, name: itemName(kind, key), price }];
  });

/** The item `id` names, or null for one the Store doesn't sell (nor has `released`). */
export const storeItem = (id: string, released: Released = NONE): StoreItem | null =>
  STORE_ITEMS.find((i) => i.id === id) ?? releasedItems(released).find((i) => i.id === id) ?? null;

/** What only verified creators have (ADR-0128): an admin's approval grants it (into `owned`); never sold. */
export const CREATOR_ITEMS: readonly ItemId[] = ['gun:creator'];

/**
 * Given by the Creator Program's milestones (ADR-0138): the Creator cap and jacket, with the Creator gun skin a set.
 * Never sold; yours only once a milestone puts it in `owned`.
 */
export const PROGRAM_ITEMS: readonly ItemId[] = ['head:creator', 'outfit:creator'];

/**
 * What a player has (ADR-0149): what they bought or were given (`owned`), and for a partner everything the Store sells
 * besides, a thing in preview while it is `released` too (never a season's, a creator's or the Creator Program's: those
 * stay theirs to give). What the server checks wearing against and sends as the account's `owned`, so a new thing in the
 * Store is every partner's at once.
 */
export const ownedBy = (
  p: { owned?: readonly string[] | undefined; partner?: boolean | undefined },
  released: Released = NONE,
): string[] => {
  const owned = p.owned ?? [];
  const sold = [...STORE_ITEMS, ...releasedItems(released)];
  return p.partner ? [...owned, ...sold.flatMap((i) => (owned.includes(i.id) ? [] : [i.id]))] : [...owned];
};

/**
 * Whether a player has `key` of `kind` (may wear it; the Store doesn't sell it to them): bought or won (`owned`), a head
 * earned by playing (its `unlock`, from the career's rounds and wins), or everyone's (neither sold, nor a head to earn,
 * nor a season's: those only the pass gives, ADR-0123; nor a creator's, ADR-0128, or the Creator Program's, ADR-0138;
 * nor in preview, ADR-0154, which is nobody's until bought).
 */
export function owns(
  kind: ItemKind,
  key: string,
  stats: { rounds: number; wins: number },
  owned: readonly string[] = [],
): boolean {
  if (owned.includes(`${kind}:${key}`)) return true;
  const u = kind === 'head' && isHeadKey(key) ? unlockOf(key) : undefined;
  if (u) return stats.rounds >= (u.rounds ?? 0) && stats.wins >= (u.wins ?? 0);
  return (
    priceOf(kind, key) === null &&
    !seasonItem(`${kind}:${key}`) &&
    !CREATOR_ITEMS.includes(`${kind}:${key}`) &&
    !PROGRAM_ITEMS.includes(`${kind}:${key}`) &&
    !isPreviewItem(`${kind}:${key}`)
  );
}

/**
 * The things `look` wears that the Store sells (its outfit; a seeded face is an everyday head's, free): a new account's
 * from the start (the look its id picks), and what every player wore when the Store opened (a migration), so nobody
 * loses what they wear.
 */
export const lookItems = (look: Look): ItemId[] =>
  priceOf('outfit', look.outfit) !== null ? [`outfit:${look.outfit}`] : [];

/** A set as `POST /api/store/buy` takes it (`set:gnome`), besides a single `ItemId`. */
export type SetId = `set:${SetKey}`;
/** The set `id` names (`set:gnome`), or null for anything else. */
export const setOf = (id: string): SetKey | null => {
  const key = id.slice(4);
  return id.startsWith('set:') && isSetKey(key) ? key : null;
};

/** The kinds a set has a piece of, in the wardrobe's order. */
export const SET_KINDS = ['head', 'outfit', 'gun'] as const;

/**
 * The things in preview that come and go with `id` (ADR-0166): the three pieces of a set in preview, else `id` alone (a
 * gun skin of its own). What the owner turns down whole, and what the daily skins routine makes official or deletes whole.
 */
export const previewUnit = (id: string): string[] => {
  const key = id.slice(id.indexOf(':') + 1);
  return isPreviewSet(key) ? SET_KINDS.map((kind) => `${kind}:${key}`) : [id];
};

/** Every unit in preview (`previewUnit`), each once, in the wardrobe's order: what the routine keeps at `PREVIEW_UNITS`. */
export const previewUnits = (): string[][] =>
  previewItems().flatMap((id) => (previewUnit(id)[0] === id ? [previewUnit(id)] : []));

/** How many units the daily skins routine keeps in preview (ADR-0166): it designs new ones up to this, never past it. */
export const PREVIEW_UNITS = 5;

/**
 * Set `key`'s pieces as the Store sells them: its head, its outfit and its gun skin; of a set in preview (ADR-0160),
 * those `released`.
 */
export const setItems = (key: SetKey, released: Released = NONE): StoreItem[] =>
  SET_KINDS.flatMap((kind) => storeItem(`${kind}:${key}`, released) ?? []);

/** Is set `key` sold whole: every piece of it in the Store (a set in preview: once all three are `released`)? */
export const setSold = (key: SetKey, released: Released = NONE): boolean =>
  setItems(key, released).length === SET_KINDS.length;

/** What a set takes off its pieces' sum. */
const SET_OFF = 0.25;

/**
 * What set `key` costs a player who has `owned` (ADR-0112): the pieces not theirs yet, a quarter off, rounded to 50
 * seeds; 0 once every piece is theirs.
 */
export function setPrice(key: SetKey, owned: readonly string[] = [], released: Released = NONE): number {
  const sum = setItems(key, released)
    .filter((i) => !owned.includes(i.id))
    .reduce((n, i) => n + i.price, 0);
  return Math.round((sum * (1 - SET_OFF)) / 50) * 50;
}

/** The set a gardener wears whole (its head's hat, keyed like the set, its outfit and its gun skin), or null. */
export const wornSet = (hat: string, look: Pick<Look, 'outfit'>, gun: string): SetKey | null =>
  SET_KEYS.find((k) => hat === k && look.outfit === k && gun === k) ?? null;
